<script lang="ts">
// Low-code data refreshes can remount the configured component without a route change.
// This module-level marker survives that remount, but is reset after a real route change.
let shownGuideVisitKey = ''
</script>

<script setup lang="ts">
import { getNewCustomerTaskCenter, joinNewCustomerTaskActivity } from '@user-center/api/newCustomerTask'
import store from '@user-center/store'
import {
  cacheNewCustomerTaskCenter,
  dismissNewCustomerTaskPopup,
  isNewCustomerTaskEnabled,
  isNewCustomerTaskPopupDismissed,
  translateNewCustomerTaskActivity,
} from '@user-center/utils/newCustomerTask'
import { $t } from '@user-center/utils/i18n'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMallShopId } from '@/composables/useMallShopId'

const props = defineProps({
  enabled: { type: Boolean, default: true },
  dismissHours: { type: Number, default: 24 },
  eyebrowText: { type: String, default: '' },
  guestButtonText: { type: String, default: '' },
  memberButtonText: { type: String, default: '' },
  laterButtonText: { type: String, default: '' },
  contentText: { type: String, default: '' },
})

const router = useRouter()
const route = useRoute()
const { shopId: mallShopId } = useMallShopId()
const activity = ref<any>(null)
const visible = ref(false)
const joinedActivityId = ref('')
const shopId = computed(() => store.getters.storeInfo?.id || mallShopId.value || 'default')
const userId = computed(() => store.getters.userinfo?.id || store.getters.userinfo?.userId || 'guest')
const isGuest = computed(() => activity.value?.loggedIn !== true)

function allTasksCompleted(nextActivity: any): boolean {
  const tasks = Array.isArray(nextActivity?.taskList) ? nextActivity.taskList : []
  return tasks.length > 0 && tasks.every((task: any) => task.actionStatus === 'COMPLETED')
}

function close(): void {
  visible.value = false
}

function dismiss(): void {
  dismissNewCustomerTaskPopup(activity.value?.activityId)
  close()
}

function beforeClose(done: () => void): void {
  dismissNewCustomerTaskPopup(activity.value?.activityId)
  done()
}

function openTaskCenter(): void {
  close()
  void router.push('/user-center/collection/newCustomerTask')
}

function openPrimaryAction(): void {
  if (!isGuest.value) {
    openTaskCenter()
    return
  }

  close()
  void router.push({
    path: '/user-center/login',
    query: {
      reg: '1',
      redirect: '/user-center/collection/newCustomerTask',
      newCustomerActivityId: String(activity.value?.activityId || ''),
    },
  })
}

async function load(): Promise<void> {
  if (!props.enabled || !isNewCustomerTaskEnabled())
    return

  try {
    let response: any = await getNewCustomerTaskCenter({ param: { shopId: shopId.value, channel: 'WEB' } })
    if (!response?.success)
      return

    let nextActivity = response.data || null
    const activityId = String(nextActivity?.activityId || '')
    if (nextActivity?.loggedIn === true && activityId && joinedActivityId.value !== activityId) {
      joinedActivityId.value = activityId
      const joinResponse: any = await joinNewCustomerTaskActivity({ param: { activityId, channel: 'WEB' } })
      if (joinResponse?.success) {
        response = await getNewCustomerTaskCenter({ param: { shopId: shopId.value, channel: 'WEB' } })
        if (response?.success)
          nextActivity = response.data || nextActivity
      }
    }

    nextActivity = await translateNewCustomerTaskActivity(nextActivity)
    const loggedIn = nextActivity?.loggedIn === true
    const eligible = loggedIn
      ? nextActivity?.newUser === true && Number(nextActivity?.newUserStatus) === 1
      : nextActivity?.headerMode === 'GUEST'
    const visitKey = `${route.fullPath}:${nextActivity?.activityId}:${userId.value}`
    const dismissed = isNewCustomerTaskPopupDismissed(nextActivity.activityId, props.dismissHours)
    if (!eligible || !nextActivity?.activityId || allTasksCompleted(nextActivity) || shownGuideVisitKey === visitKey || dismissed)
      return

    cacheNewCustomerTaskCenter(nextActivity)
    activity.value = nextActivity
    shownGuideVisitKey = visitKey
    visible.value = true
  }
  catch {
    visible.value = false
  }
}

onMounted(() => {
  void load()
  window.addEventListener('mall-auth-token-changed', reloadAfterLogin)
})

onBeforeUnmount(() => {
  window.removeEventListener('mall-auth-token-changed', reloadAfterLogin)
})

function reloadAfterLogin(): void {
  // Login updates tokens without remounting the root homepage. Re-query the
  // authoritative task-center response so the guest guide switches to the
  // logged-in new-customer guide immediately.
  shownGuideVisitKey = ''
  visible.value = false
  activity.value = null
  void load()
}

watch(
  () => route.fullPath,
  () => {
    reloadAfterLogin()
  },
)
</script>

<template>
  <el-dialog v-if="visible" v-model="visible" append-to-body width="420px" align-center :show-close="false" :before-close="beforeClose" class="new-customer-task-guide-popup">
    <div class="new-customer-task-guide-popup__header">
      <button class="new-customer-task-guide-popup__close" type="button" :aria-label="$t('userCenter.newCustomerTask.close')" @click="dismiss">
        ×
      </button>
      <span class="new-customer-task-guide-popup__eyebrow">{{ eyebrowText || $t('userCenter.newCustomerTask.benefit') }}</span>
      <h2>{{ activity?.benefitTitle || $t('userCenter.newCustomerTask.popupTitle') }}</h2>
      <p>{{ isGuest ? activity?.guestPrompt : activity?.benefitSubtitle }}</p>
    </div>
    <div class="new-customer-task-guide-popup__content">
      <p>{{ contentText || $t('userCenter.newCustomerTask.popupDescription') }}</p>
      <el-button type="primary" size="large" @click="openPrimaryAction">
        {{ isGuest ? (guestButtonText || $t('userCenter.newCustomerTask.registerNow')) : (memberButtonText || $t('userCenter.newCustomerTask.viewBenefits')) }}
      </el-button>
      <el-button link @click="dismiss">
        {{ laterButtonText || $t('userCenter.newCustomerTask.notNow') }}
      </el-button>
    </div>
  </el-dialog>
</template>

<style scoped lang="scss">
:deep(.new-customer-task-guide-popup) {
  max-width: calc(100vw - 32px);
  overflow: hidden;
  border-radius: 18px;
  box-shadow: 0 24px 64px rgba(27, 42, 63, .24);
}

:deep(.new-customer-task-guide-popup .el-dialog__header) {
  display: none;
}

:deep(.new-customer-task-guide-popup .el-dialog__body) {
  padding: 0;
}

.new-customer-task-guide-popup__header {
  position: relative;
  padding: 30px 34px 28px;
  overflow: hidden;
  color: #fff;
  background:
    radial-gradient(circle at 100% 0, rgba(255, 255, 255, .22), transparent 34%),
    linear-gradient(135deg, #ff9b3d 0%, #ff6a37 48%, #ff4d2f 100%);
}

.new-customer-task-guide-popup__header::after {
  position: absolute;
  right: -46px;
  bottom: -88px;
  width: 184px;
  height: 184px;
  content: '';
  border: 1px solid rgba(255, 255, 255, .22);
  border-radius: 50%;
}

.new-customer-task-guide-popup__eyebrow {
  position: relative;
  z-index: 1;
  display: inline-flex;
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  background: rgba(255, 255, 255, .18);
  border: 1px solid rgba(255, 255, 255, .24);
  border-radius: 999px;
}

.new-customer-task-guide-popup__header h2,
.new-customer-task-guide-popup__header p {
  position: relative;
  z-index: 1;
  margin: 0;
}

.new-customer-task-guide-popup__header h2 {
  margin-top: 14px;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: .5px;
}

.new-customer-task-guide-popup__header p {
  margin-top: 9px;
  font-size: 15px;
  line-height: 1.5;
  opacity: .9;
}

.new-customer-task-guide-popup__close {
  position: absolute;
  z-index: 2;
  top: 16px;
  right: 18px;
  width: 28px;
  height: 28px;
  padding: 0;
  color: #fff;
  font-size: 27px;
  font-weight: 200;
  line-height: 24px;
  cursor: pointer;
  background: rgba(255, 255, 255, .12);
  border: 0;
  border-radius: 50%;
}

.new-customer-task-guide-popup__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 13px;
  padding: 30px 34px 28px;
  color: #59677a;
  text-align: center;
}

.new-customer-task-guide-popup__content > p {
  margin: 0 0 6px;
  font-size: 16px;
  line-height: 1.6;
}

.new-customer-task-guide-popup__content .el-button--primary {
  width: 100%;
  height: 50px;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(90deg, #ff7a20, #ff4e20);
  border: 0;
  border-radius: 10px;
  box-shadow: 0 8px 18px rgba(255, 91, 37, .25);
}

.new-customer-task-guide-popup__content .el-button--link {
  height: auto;
  margin: 0;
  padding: 3px 10px;
  color: #7a8492;
  font-size: 15px;
}

.new-customer-task-guide-popup__content .el-button--link.el-button--primary {
  width: auto;
  color: #f2662f;
  background: transparent;
  box-shadow: none;
}

@media (max-width: 480px) {
  .new-customer-task-guide-popup__header {
    padding: 26px 26px 24px;
  }

  .new-customer-task-guide-popup__content {
    padding: 26px 24px 20px;
  }

  .new-customer-task-guide-popup__header h2 {
    font-size: 24px;
  }
}
</style>
